import { useMemo } from 'react';
import { Link } from 'react-router';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/entities/user';
import {
  FileDetail,
  FileUploader,
  KnowledgeBaseDocumentsTable,
  KnowledgeBaseSelectionBar,
  useKnowledgeBaseDetail,
} from '@/features/knowledge-bases';
import { useKbSharedWith } from '@/features/knowledge-bases/model/useKbSharedWith/useKbSharedWith';
import {
  getCollectionVisibilityLabel,
  resolveCollectionVisibility,
} from '@/features/knowledge-bases/lib';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { DeleteConfirmationModal } from '@/shared/ui';
import { PaginationControls } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { CollectionPaneHeader } from './CollectionPaneHeader';
import { CollectionFilesToolbar } from './CollectionFilesToolbar';
import { EmptyCollectionFiles } from './EmptyCollectionFiles';
import { useCollectionFilesPane } from './hooks/useCollectionFilesPane';

export function CollectionFilesPane() {
  const { t } = useTranslation('knowledge');

  const { knowledgeBase, isLoading: isLoadingKnowledgeBase, isError } = useKnowledgeBaseDetail();
  const {
    canViewAnyKnowledgeBase,
    canViewFoundationalKnowledgeBases,
    canViewWorkspaceKnowledgeBases,
    canViewOrganizationKnowledgeBases,
    canManageAnyKnowledgeBase,
    canManageFoundationalKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canManageOrganizationKnowledgeBases,
  } = usePermissions();
  const { editKnowledgeBaseModal } = useKnowledgeBaseModal();

  const pane = useCollectionFilesPane();
  const sharedWith = useKbSharedWith(knowledgeBase);

  const knowledgeBaseVisibility = useMemo(
    () => resolveCollectionVisibility(knowledgeBase ?? {}),
    [knowledgeBase],
  );

  const canViewCollection = useMemo(() => {
    if (!knowledgeBase) return true;
    if (!canViewAnyKnowledgeBase) return false;
    if (knowledgeBase.isInternal) return canViewFoundationalKnowledgeBases;
    if (knowledgeBase.workspaceId) return canViewWorkspaceKnowledgeBases;
    return canViewOrganizationKnowledgeBases;
  }, [
    knowledgeBase,
    canViewAnyKnowledgeBase,
    canViewFoundationalKnowledgeBases,
    canViewWorkspaceKnowledgeBases,
    canViewOrganizationKnowledgeBases,
  ]);

  const canEditCollection = useMemo(() => {
    if (!knowledgeBase) return false;
    if (!canManageAnyKnowledgeBase) return false;
    if (knowledgeBase.isInternal) return canManageFoundationalKnowledgeBases;
    if (knowledgeBase.workspaceId) return canManageWorkspaceKnowledgeBases;
    return canManageOrganizationKnowledgeBases;
  }, [
    knowledgeBase,
    canManageAnyKnowledgeBase,
    canManageFoundationalKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canManageOrganizationKnowledgeBases,
  ]);

  // Undefined until the collection resolves: `resolveCollectionVisibility` reads an absent
  // one as organization, which would render "Organization" during the fetch and then
  // correct itself — that reads as a wrong value rather than as a pending one.
  const visibilityLabel = knowledgeBase
    ? getCollectionVisibilityLabel(t, knowledgeBase)
    : undefined;

  const isUnavailable =
    !isLoadingKnowledgeBase && (!knowledgeBase || isError || !canViewCollection);

  // A collection nobody has uploaded to yet, as opposed to a search that found nothing:
  // the first has nothing to search, the second still does.
  const isEmptyCollection =
    !isLoadingKnowledgeBase &&
    !pane.isLoadingFiles &&
    !pane.hasActiveSearch &&
    pane.totalCount === 0;

  if (isUnavailable) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{t('knowledgeBases.collectionFilesPane.collectionUnavailable')}</EmptyTitle>
          <EmptyDescription>
            <Link to={ROUTES.KNOWLEDGE_HUB_KB} className="text-primary underline">
              {t('knowledgeBases.collectionFilesPane.backToCollections')}
            </Link>
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div
      data-testid="collection-files-pane"
      className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl"
    >
      <div data-testid="collection-files-pane-top" className="flex flex-col gap-xl">
        <CollectionPaneHeader
          name={knowledgeBase?.name ?? t('knowledgeBases.knowledgeBaseDocumentsTable.untitled')}
          description={knowledgeBase?.description}
          visibility={knowledgeBaseVisibility}
          visibilityLabel={visibilityLabel}
          workspaceCount={knowledgeBase?.workspaceCount}
          isLoading={isLoadingKnowledgeBase}
          canEdit={canEditCollection}
          onEdit={editKnowledgeBaseModal}
          info={{
            createdByUsername: knowledgeBase?.createdByUsername,
            updated: knowledgeBase?.updated ? formatRelativeDate(knowledgeBase.updated) : undefined,
            sharedWith,
          }}
        />
        {/* Nothing to narrow in a collection nobody has uploaded to, and no rows to select
            either, so the whole band goes and the empty state carries upload instead. */}
        {!isEmptyCollection && (
          <div className="relative">
            <CollectionFilesToolbar
              searchQuery={pane.searchQuery}
              onSearchChange={pane.handlers.onSearchChange}
              onUpload={pane.handlers.onOpenFileUploader}
              canUpload={canEditCollection}
            />
            {pane.selection.hasSelection && (
              <div className="absolute inset-0 flex items-center bg-card">
                <KnowledgeBaseSelectionBar
                  selection={pane.selection}
                  totalCount={pane.totalCount}
                  onDownload={pane.handlers.onBulkDownload}
                  isDownloading={pane.isDownloading}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {isEmptyCollection ? (
        <EmptyCollectionFiles
          onUpload={pane.handlers.onOpenFileUploader}
          canUpload={canEditCollection}
        />
      ) : (
        <div>
          <KnowledgeBaseDocumentsTable
            rows={pane.files}
            onViewDetail={pane.handlers.onViewDetail}
            onDownload={pane.handlers.onDownloadRow}
            onDelete={pane.handlers.onDeleteRow}
            isLoading={pane.isLoadingFiles}
            sortBy={pane.sortBy}
            sortDirection={pane.sortOrder}
            onSort={pane.handlers.onSort}
            canModify={pane.canModify}
            selection={pane.selection}
            fileTypeColumnVariant="type"
          />
          <PaginationControls
            page={pane.page}
            totalItems={pane.totalCount}
            pageSize={10}
            onPageChange={pane.handlers.onPageChange}
            className="py-3"
          />
        </div>
      )}
      {knowledgeBase && (
        <FileUploader
          open={pane.dialogs.fileUploaderOpen}
          onClose={pane.handlers.onCloseFileUploader}
          title={t('knowledgeBaseDetail.uploadFilesTo', { name: knowledgeBase.name ?? '' })}
          onAddDocuments={pane.handlers.onUpload}
        />
      )}
      {pane.dialogs.selectedFile && (
        <FileDetail
          file={pane.dialogs.selectedFile}
          open={pane.dialogs.fileDetailOpen}
          onClose={pane.handlers.onCloseFileDetail}
          title={t('knowledgeBaseDetail.documentDetail')}
        />
      )}
      {pane.dialogs.selectedFile && (
        <DeleteConfirmationModal
          open={pane.dialogs.fileDeleterOpen}
          isDeleting={pane.dialogs.isDeleting}
          onCancel={pane.handlers.onCloseFileDeleter}
          onConfirmDelete={pane.handlers.onConfirmDelete}
          resourceType={t('knowledgeBaseDetail.documentResource')}
          resourceName={pane.dialogs.selectedFile.fileName}
          warningMessage={t('knowledgeBaseDetail.thisActionIsIrreversibleBy')}
        />
      )}
    </div>
  );
}

CollectionFilesPane.displayName = 'CollectionFilesPane';
